import {
  Button,
  Divider,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { Fragment, useMemo } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import {
  ItemDetailBoxFragment,
  Maybe,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  serials: Array<UserItemSerialDetailFragment>
  item: Maybe<ItemDetailBoxFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = { onSubmit: () => Promise<void> }

export const ResaleCreateConfirmationTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { t } = useTranslation('resaleCreateConfirmation')
  const { routerPages, router, generatePath, routerNames } = useRouter<{
    itemId: string
  }>()
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleExhibit'))

  return (
    <BaseLayout
      margin
      header={{
        back: true,
        title: t('title'),
        defaultBackPage: generatePath(routerPages.ResaleApplicationCreate, {
          itemId: router.query.itemId,
        }),
        right: (
          <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
        ),
      }}
    >
      <Head title={routerNames.ResaleCreateConfirmation} />
      {state.loading ? (
        <ResaleConfirmationSkeletonCell />
      ) : (
        <>
          <ResaleTitleCell />
          <ConfirmationContentCell item={state.item} serials={state.serials} />
          <SubmitButtonCell submitting={state.submitting} onSubmit={onSubmit} />
        </>
      )}
    </BaseLayout>
  )
}

const ResaleTitleCell: Cell = () => {
  const { t } = useTranslation('resaleCreateConfirmation')
  return (
    <CellFrame sx={{ py: 5 }}>
      <Typography variant="h4">{t('resaleInfo')}</Typography>
    </CellFrame>
  )
}

const ResaleConfirmationSkeletonCell: Cell = () => (
  <CellFrame>
    <SkeletonText variant="h4" width="25%" />
    <Stack sx={{ mt: 8, bgcolor: 'grey.50' }}>
      <Skeleton
        variant="rectangular"
        height={20}
        width="100%"
        sx={{ bgcolor: 'grey.100', my: '6px' }}
      />
    </Stack>
    <Stack sx={{ pt: 7 }}>
      <SkeletonText variant="small" numberOfLine={2} />
      <Stack sx={{ px: 5, py: 4 }}>
        <Divider />
      </Stack>
      <SkeletonText variant="small" numberOfLine={2} />
    </Stack>
    <Stack sx={{ mt: '60px', bgcolor: 'grey.50' }}>
      <Skeleton
        variant="rectangular"
        height={20}
        width="100%"
        sx={{ bgcolor: 'grey.100', my: '6px' }}
      />
    </Stack>
    <SkeletonText variant="small" numberOfLine={2} />
    <Stack spacing={2} sx={{ pt: '34px' }}>
      <Stack direction="row" justifyContent="space-between">
        <SkeletonText variant="small" width="30%" />
        <SkeletonText variant="small" width="30%" />
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between">
        <SkeletonText variant="small" width="30%" />
        <SkeletonText variant="small" width="30%" />
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between">
        <SkeletonText variant="small" width="30%" />
        <SkeletonText variant="small" width="30%" />
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between">
        <SkeletonText variant="small" width="30%" />
        <SkeletonText variant="small" width="30%" />
      </Stack>
      <Stack alignItems="center" sx={{ pt: 8, pb: 6 }}>
        <Skeleton
          variant="rectangular"
          height={44}
          width={212}
          sx={{ borderRadius: 34 }}
        />
        <SkeletonText variant="caption" width="15%" sx={{ mt: 2 }} />
      </Stack>
    </Stack>
  </CellFrame>
)

type ConfirmationContentCellProps = {
  item: State['item']
  serials: Array<UserItemSerialDetailFragment>
}
const ConfirmationContentCell: Cell<ConfirmationContentCellProps> = ({
  item,
  serials,
}) => {
  const { t } = useTranslation('resaleCreateConfirmation')
  const ticketQuantity = serials.length
  //整理番号が設定されているか判定
  const isTicketAdmission = !!item?.metadata?.ticket?.assignedNumberPrefix
  const { price } = useMemo(() => getItemSkuPrice(item), [item])

  const data = [
    {
      label: t('ticketPrice'),
      value: `￥${numeral(price * ticketQuantity).format('0,0')}`,
    },
    {
      // リセール手数料 = チケット代金 * 代金に対する割合 * 枚数
      label: t('resaleFee'),
      value: `-¥${numeral(
        price *
          (item?.metadata?.ticket?.resaleFee?.feeRatePerTicket || 0) *
          ticketQuantity
      ).format('0,0')}`,
    },
    {
      // 振り込み手数料 = 1注文あたりの振り込み手数料
      label: t('transferFee'),
      value: `-¥${numeral(
        item?.metadata?.ticket?.resaleFee?.bankTransferFeePerOrder || 0
      ).format('0,0')}`,
    },
  ]
  const totalAmount = useMemo(() => {
    if (!price) {
      return 0
    }
    return numeral(
      price * ticketQuantity * 0.9 -
        (item?.metadata?.ticket?.resaleFee?.bankTransferFeePerOrder || 0)
    ).format('0,0')
  }, [
    item?.metadata?.ticket?.resaleFee?.bankTransferFeePerOrder,
    price,
    ticketQuantity,
  ])

  return (
    <CellFrame>
      <Stack spacing={3}>
        <Typography
          variant="h6"
          sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}
          color="text.secondary"
        >
          {t('exhibitionContent')}
        </Typography>
        <Stack spacing={1}>
          <Typography variant="subtitle1" color="text.secondary">
            {t('ticketName')}
          </Typography>
          <Typography variant="body2">{item?.title}</Typography>
          {!!item?.sku?.unitName && (
            <Typography variant="body2">{item?.sku?.unitName}</Typography>
          )}
        </Stack>
        <Divider />
        <Stack>
          <Typography variant="subtitle1" color="text.secondary">
            {t(
              isTicketAdmission ? 'serialTicketNumberList' : 'serialNumberList'
            )}
          </Typography>
          <Stack>
            <List dense sx={{ listStyleType: 'disc', pl: 5 }}>
              {serials.map(serial => (
                <ListItem
                  disableGutters
                  sx={{ display: 'list-item' }}
                  key={serial.id}
                >
                  <Typography variant="body2">
                    {serial.serial?.formattedAssignedNumber ??
                      serial.formattedSerialNumber}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ pt: 9 }}>
        <Typography
          variant="h6"
          sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}
          color="text.secondary"
        >
          {t('ticketPayment')}
        </Typography>
        <Stack sx={{ pt: 3 }}>
          <Typography variant="body2">{t('ticketPaymentDesc')}</Typography>
        </Stack>
        <Stack sx={{ pt: 3 }}>
          {data.map(({ value, label }, index) => (
            <Fragment key={index}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ py: 2 }}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Stack>
              <Divider />
            </Fragment>
          ))}
          <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
            <Typography variant="h6">{t('total')}</Typography>
            <Typography variant="h6">¥{totalAmount}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const SubmitButtonCell: Cell<{
  submitting: boolean
  onSubmit: Action['onSubmit']
}> = ({ submitting, onSubmit }) => {
  const { t } = useTranslation('resaleCreateConfirmation')
  const { router, routerPages, generatePath } = useRouter<{
    itemId: string
  }>()
  return (
    <CellFrame>
      <Stack spacing={3} alignItems="center" sx={{ pt: 9, pb: 7 }}>
        <Button
          size="large"
          sx={{ width: 212 }}
          loading={submitting}
          onClick={onSubmit}
        >
          {t('submit')}
        </Button>
        <Button
          variant="text"
          sx={{ width: 212 }}
          onClick={() =>
            router.goBack(
              generatePath(routerPages.ResaleApplicationCreate, {
                itemId: router.query.itemId,
              })
            )
          }
        >
          {t('back')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
